/**
 * `/o/:org/skills/:id/edit` - the guided editor.
 *
 * V1 section 9 requires a guided editor with a source toggle, a file list, a
 * preview, a save state and conflict recovery, and it requires that "Save draft"
 * be distinct from "Submit".
 *
 * What this build can honestly do here is narrower, and the screen says so in
 * the place a person would otherwise press a button:
 *
 *   - A draft body is readable and writable through `GET /v1/drafts/:id/files`
 *     and the save route, so this screen holds the document in a real textarea
 *     and "Save draft" sends its bytes as the one file it edits. What that
 *     textarea holds is the DRAFT's text: it is not the published bundle below
 *     it, and the two are separate fields because they are separate facts.
 *   - Submitting for review is a state transition on the draft row and is real.
 */

import { type Failure, fail, ok, type Result } from '../api/client.js';
import * as api from '../api/queries.js';
import { numericValue } from '../api/queries.js';
import type { AppShell, ScreenState } from '../app.js';
import {
  DRAFT_STATE_LABEL,
  formatBytes,
  formatDateTime,
  personLabel,
  shortHash,
} from '../lib/format.js';
import { html, type RawHtml, raw } from '../lib/html.js';
import { renderMarkdown } from '../lib/markdown.js';
import { button, docHead, failurePanel, skeletonRows, tag } from '../ui/components.js';

export interface EditorData {
  readonly skill: api.SkillRow | null;
  readonly drafts: readonly api.DraftRow[];
  readonly draftFiles: readonly api.DraftFileRow[];
  readonly active: api.DraftRow | null;
  readonly source: string | null;
  /**
   * Why there is no source, when there is no source.
   *
   * `source: null` means "this release has no bytes". Without this field it also
   * meant "the bundle would not download", and those are different facts to a
   * person: one is a property of the release, the other is a fault on this
   * screen. Collapsing them produced a confident wrong answer — a failed
   * download rendering as "there is no published release to show" — and nothing
   * anywhere went red, because the failure had been handled correctly and then
   * discarded one line from where it was produced.
   */
  readonly sourceFailure: Failure | null;
  /**
   * The editable body of the ACTIVE DRAFT, as the draft route stores it.
   *
   * Deliberately not `source`. `source` is the bytes of the PUBLISHED bundle,
   * a property of a release nobody on this screen can edit; this is text a
   * person is about to overwrite a draft with. Overloading one field with both
   * would make "the document" mean whichever the caller last wrote into it,
   * and the editor would then save the published text over the draft.
   */
  readonly draftSource: string | null;
  /**
   * Why the draft body could not be read, when it could not be read.
   *
   * Same three-way distinction `source` makes, and the same reason: a draft
   * read that failed is NOT an empty document. Rendering it as one hands
   * someone an empty box over a draft they cannot see, and then lets them
   * "save" that emptiness over real content.
   */
  readonly draftSourceFailure: Failure | null;
  /**
   * Why the last attempt to create a draft here was refused, when it was.
   *
   * Not a load result: `loadSkillEditor` cannot know it, and a screen that
   * reloads after a refused create must not lose the reason. The handler puts
   * it here and the panel renders it through `failurePanel`, beside the control
   * that failed, so the person is told what the server said and offered the
   * same button again.
   *
   * Optional because an action writes it rather than the loader, and
   * `EditorData` reaches this view as `unknown` — so `createFailureOf` checks
   * instead of trusting the declaration.
   */
  readonly createDraftFailure?: Failure | null;
}

/**
 * The one path this editor reads and writes.
 *
 * The save route takes a list of files, and this screen edits one document, so
 * the path is stated once here rather than spelled at both ends of a round trip
 * that has to agree on it.
 */
export const DRAFT_DOCUMENT_PATH = 'SKILL.md';

export async function loadSkillEditor(
  app: AppShell,
  orgId: string,
  skillId: string,
): Promise<Result<EditorData>> {
  void orgId;
  const skills = await api.loadSkills(app.client, [skillId]);
  if (!skills.ok) return skills;
  const skill = skills.value[0] ?? null;
  if (skill === null) return fail({ kind: 'not-found', reason: 'not_found' });

  const draftList = await api.listSkillDrafts(app.client, skillId);
  if (!draftList.ok) return draftList;

  const active =
    draftList.value.find((draft) => draft.state === 'draft') ?? draftList.value[0] ?? null;
  const files =
    active === null
      ? ok<readonly api.DraftFileRow[]>([])
      : await api.listDraftFiles(app.client, active.id);
  if (!files.ok) return files;

  let source: string | null = null;
  let sourceFailure: Failure | null = null;
  if (skill.current_release_id !== null) {
    const bundle = await api.downloadBundle(app.client, skillId);
    // Both halves are carried. `source` keeps meaning "the bytes we have" and
    // `sourceFailure` keeps meaning "why we have none", so the view can tell a
    // release with no document from a document this screen failed to fetch.
    source = bundle.ok ? bundle.value : null;
    sourceFailure = bundle.ok ? null : bundle.failure;
  }

  // Both halves are carried, for the same reason and with the same reading:
  // `draftSource` means "the bytes we have" and `draftSourceFailure` means "why
  // we have none". A draft whose body would not read must reach the view as a
  // failure, not as an empty document someone can then save over.
  //
  // THREE states, not two, and the file list is what tells them apart. A brand
  // new draft has no `draft_files` row at all, and the bytes route only ever
  // serves a row that is in the list — so asking for it answers 404
  // `draft_not_found`, which is TRUE OF THE FILE and reads on screen as "this
  // draft does not exist". It does exist; it is on this page with a version and
  // an author. A body that has never been written is not a body that could not
  // be read, and collapsing the two tells someone their new draft is missing
  // and leaves them no textarea to type into.
  //
  // So the read is attempted only when the list says the row is there. Absent
  // from the list means empty, and empty means the editor offers the textarea.
  // Present in the list and failing to read is the case the failure panel
  // exists for, and it is unchanged.
  let draftSource: string | null = null;
  let draftSourceFailure: Failure | null = null;
  const hasDocument = files.value.some((file) => file.path === DRAFT_DOCUMENT_PATH);
  if (active !== null && hasDocument) {
    const body = await api.readDraftFile(app.client, active.id, DRAFT_DOCUMENT_PATH);
    draftSource = body.ok ? body.value : null;
    draftSourceFailure = body.ok ? null : body.failure;
  } else if (active !== null) {
    // A draft that has never been saved has an empty document, and saying so
    // with a string rather than a failure is what keeps the textarea reachable.
    draftSource = '';
  }

  return ok({
    skill,
    drafts: draftList.value,
    draftFiles: files.value,
    active,
    source,
    sourceFailure,
    draftSource,
    draftSourceFailure,
    createDraftFailure: null,
  });
}

export function renderSkillEditor(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Editor' }], 'Edit')}
      <div class="panel">${skeletonRows(['Field', 'Value'], 4)}</div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Editor' }], 'Edit')}
      ${failurePanel(screen.failure, { retry: 'editor:reload' })}
    `;
  }

  const data = screen.data as EditorData;
  const draft = data.active;
  const viewerId = app.state.session?.userId ?? '';
  const isAuthor = draft !== null && draft.author_id === viewerId;
  const createFailure = draft === null ? createFailureOf(data) : null;

  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Editor' },
        { label: 'Skill', value: data.skill?.slug ?? '' },
        { label: 'Draft', value: draft === null ? 'none' : draft.id.slice(0, 8) },
      ],
      `Editing ${data.skill?.title ?? 'a skill'}`,
      'A draft is a revision workspace. Saving it and submitting it for review are two different actions and this screen keeps them apart.',
    )}

    ${
      draft === null
        ? html`<div class="panel">
          <div class="panel-head">
            <h2>No draft for this skill</h2>
          </div>
          <div class="panel-body stack-sm">
            <p class="cell-note">
              A draft is the private workspace a revision is written in. Nothing has been started
              for this skill, so there is nothing here to edit yet.
            </p>
            <div class="btn-row">
              ${button('Create draft', 'editor:create', 'primary')}
            </div>
            ${
              // A create that was refused renders as a refusal, in place, with a
              // retry that presses the same control again. It is never folded
              // into "no draft" — that would tell a person their click did
              // nothing when in fact the server refused them, and the two are
              // different facts.
              createFailure === null
                ? ''
                : failurePanel(createFailure, { retry: 'editor:create' })
            }
          </div>
        </div>`
        : html`
          <div class="stack">
            ${previewPanel(app, data)}
            <div class="split">
              <div>
                ${draftDocumentPanel(app, data, isAuthor)}
                ${fileListPanel(data)}
                ${sourcePanel(data)}
              </div>
              <div>
                ${statePanel(app, draft, isAuthor)}
                ${savePanel(app, draft, isAuthor)}
              </div>
            </div>
          </div>
        `
    }
  `;
}

/**
 * Step 2 of the journey: what this skill will look like to whoever uses it.
 *
 * WHAT A PREVIEW IS HERE, ARGUED RATHER THAN ASSUMED. Three things could sit
 * here and only one of them answers the question an author has at this point
 * in the journey, which is "is this the document I meant to write?":
 *
 *   - the FILE LIST is already on this screen, as "Files in the draft", and it
 *     answers "what else is in the package" — a different question.
 *   - the MANIFEST THAT WOULD BE PUBLISHED is a fact about publication, and
 *     publication is a separate explicit step. Putting it here would invite the
 *     reading that approving a draft publishes it, which is the exact confusion
 *     the journey is ordered to prevent.
 *   - the RENDERED GUIDE is the thing being written. An author who has just
 *     rewrapped a paragraph needs to see it rendered, because the wrapping is
 *     what they were actually looking at while typing.
 *
 * So the preview is the rendered document, and the file inventory that would
 * travel with it is shown beside it as the second half of "what would happen".
 *
 * THE THREE SAFETY PROPERTIES, and how each is enforced rather than asserted in
 * prose:
 *
 *   1. IT DOES NOT PUBLISH. This panel is a pure function of `app.state.source`
 *      and the loaded draft rows. It issues no request of any kind, so there is
 *      no code path from here to the publish route. The screen says so too,
 *      because a preview that looks like a release is a preview someone will
 *      trust wrongly.
 *   2. IT DOES NOT MUTATE. Same mechanism: zero requests. It also carries no
 *      `data-action` attribute at all, so it cannot be a control even if
 *      something later tried to bind one to it.
 *   3. IT SHOWS WHAT WOULD HAPPEN. It renders the CURRENT text in the editor,
 *      including text that has not been saved — and it says which of those it
 *      is showing. A preview of the last saved bytes while the author is looking
 *      at newer unsaved bytes would be a preview of something else.
 *
 * Rendering goes through `renderMarkdown`, which escapes before it formats, so
 * raw HTML in a draft is displayed as text and cannot execute in the author's
 * browser. That is V1 §6's "render Markdown with raw HTML disabled" applied to
 * the surface that exists to show a stranger's markdown to someone deciding
 * whether to publish it.
 */
function previewPanel(app: AppShell, data: EditorData): RawHtml {
  const source = app.state.source;
  const stored = draftBytesOf(data);
  const unsaved = stored !== null && stored !== source;
  const unreadable = draftFailureOf(data) !== null;

  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Preview</h2>
        <span class="cell-meta">rendered · ${DRAFT_DOCUMENT_PATH}</span>
      </div>
      <div class="panel-body stack-sm">
        <article class="prose">${raw(renderMarkdown(source))}</article>
        ${unreadable
          ? html`<div class="callout callout-attention">
              <span class="callout-tag">Showing nothing stored</span>
              <p>
                This draft's stored document could not be read, so the preview above is your unsaved
                text rather than what is on the server. Saving will replace whatever is stored.
              </p>
            </div>`
          : unsaved
            ? html`<div class="callout callout-attention">
                <span class="callout-tag">Unsaved text</span>
                <p>
                  This preview shows the text in the editor, which is not yet saved. The stored copy
                  still holds the version you last saved.
                </p>
              </div>`
            : html`<p class="cell-note">
                This preview shows the text in the editor, which matches what is stored.
              </p>`}
        <h3>What would travel with it</h3>
        ${
          data.draftFiles.length === 0
            ? html`<p class="cell-note">
                This draft has no files stored yet, so there is nothing to publish but the document
                above.
              </p>`
            : html`<ul class="file-list">
                ${data.draftFiles.map(
                  (file) => html`<li>
                    <span>${file.path}</span>
                    <span>${formatBytes(numericValue(file.bytes))} · ${shortHash(file.content_hash)}</span>
                  </li>`,
                )}
              </ul>`
        }
        <p class="cell-note">
          Previewing publishes nothing and changes nothing. It is this document rendered, plus the
          files that would travel with it — which is what a reviewer will be shown before anything is
          released to anyone.
        </p>
      </div>
    </div>
  `;
}

/**
 * The draft's own document: a real textarea over the bytes the draft route
 * stores, and the only control on this screen that writes them.
 *
 * The value comes from `app.state.source`, not from `EditorData`. `paint()`
 * replaces `innerHTML` on every state change — including the one every
 * keystroke makes — so a textarea rendered from a field that is not in state
 * loses every character as it is typed. State is where the text lives; the
 * load is what put the stored bytes there.
 */
function draftDocumentPanel(app: AppShell, data: EditorData, isAuthor: boolean): RawHtml {
  const bytes = draftBytesOf(data);
  const failure = draftFailureOf(data);
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Draft document</h2>
        <span class="cell-meta">${DRAFT_DOCUMENT_PATH}</span>
      </div>
      <div class="panel-body stack-sm">
        ${
          // A body this screen could not read is not an empty body, so there is
          // no box to type into: an empty box here reads as "this draft is
          // empty", and saving it would overwrite content nobody could see.
          failure !== null
            ? failurePanel(failure, { retry: 'editor:reload' })
            : html`
                <label class="field-label" for="editor-source">${DRAFT_DOCUMENT_PATH}</label>
                <textarea
                  class="field"
                  id="editor-source"
                  rows="18"
                  data-field="editor.source"
                  placeholder="The document this skill teaches"
                  ${isAuthor ? '' : 'disabled'}
                >${app.state.source}</textarea>
                ${
                  bytes === null
                    ? html`<p class="cell-note">
                        This draft has no ${DRAFT_DOCUMENT_PATH} stored yet. Type the document and
                        save it; nothing is written until Save draft reports a version.
                      </p>`
                    : ''
                }
              `
        }
        <p class="cell-note">
          This is the draft, not the published release. The published document is below, and saving
          here changes nothing a reader can see until the draft is approved and published.
        </p>
      </div>
    </div>
  `;
}

/**
 * The stored draft bytes, or null when the payload carries none.
 *
 * `EditorData` reaches this view as `ScreenState`'s `unknown`, so its declared
 * types are enforced nowhere on the way in: an omitted key arrives as
 * `undefined`, not as the `null` the type promises. Both are checked explicitly
 * rather than with the loose `!= null` `sourcePanel` needs — that comparison is
 * load-bearing there for a reason recorded in place, and it is not a pattern to
 * copy into a second branch.
 */
function draftBytesOf(data: EditorData): string | null {
  const bytes: unknown = data.draftSource;
  return typeof bytes === 'string' ? bytes : null;
}

/** The reason the draft body is unreadable, or null when it was not attempted or did read. */
function draftFailureOf(data: EditorData): Failure | null {
  const failure: unknown = data.draftSourceFailure;
  return typeof failure === 'object' && failure !== null ? (failure as Failure) : null;
}

/**
 * Why the last create was refused, or null when none was or it succeeded.
 *
 * Same unchecked boundary as `draftFailureOf`, and for the same reason: the
 * payload arrives as `unknown`, so `undefined` is reachable where the type says
 * `Failure | null` and both must read as "no failure".
 */
function createFailureOf(data: EditorData): Failure | null {
  const failure: unknown = data.createDraftFailure;
  return typeof failure === 'object' && failure !== null ? (failure as Failure) : null;
}

function fileListPanel(data: EditorData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Files in the draft</h2>
        <span class="cell-meta">${data.draftFiles.length}</span>
      </div>
      <div class="panel-body">
        ${
          data.draftFiles.length === 0
            ? html`<p class="cell-note">This draft has no files yet.</p>`
            : html`<ul class="file-list">
              ${data.draftFiles.map(
                (file) => html`<li>
                  <span>${file.path}</span>
                  <span>${formatBytes(numericValue(file.bytes))} · ${shortHash(file.content_hash)}</span>
                </li>`,
              )}
            </ul>`
        }
        <p class="cell-note">
          ${DRAFT_DOCUMENT_PATH} is the file the editor above reads and writes. This list is the
          record of what the draft holds, with the size and digest of each file as they are stored.
        </p>
      </div>
    </div>
  `;
}

function sourcePanel(data: EditorData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Published document</h2>
        <span class="cell-meta">source</span>
      </div>
      <div class="panel-body">
        ${
          data.source !== null
            ? html`<pre class="diff"><code>${data.source}</code></pre>`
            : data.sourceFailure != null // loose on purpose: `EditorData` reaches
              ? // this view as an `unknown` payload, so nothing enforces the declared
                // `Failure | null` at the boundary this branch actually reads. An
                // omitted key arrives as `undefined`, which `!== null` would accept
                // and hand to `presentFailure(undefined)` — a render-time TypeError
                // instead of the empty state. `!= null` means both are "no failure".
                // A download that failed is NOT an absent document. This is the
                // case that used to render as "no published release" and told a
                // person their release was empty when it was unreachable. It
                // reuses the shared failure vocabulary rather than inventing a
                // fourth rendering of "something went wrong".
                failurePanel(data.sourceFailure, { retry: 'editor:reload' })
              : html`<p class="cell-note">There is no published release to show.</p>`
        }
      </div>
    </div>
  `;
}

/**
 * The states whose next step is PUBLICATION rather than editing.
 *
 * The publication control lives in exactly one place — the review screen,
 * where `publishAvailability` decides whether Publish may be pressed at all
 * (`views/publication.ts`). A second control here would be a second
 * availability rule that could disagree with the first, which is the failure
 * `@PublishControl` removed when two call sites existed. So this panel states
 * the state and names the one door that leads to the reason and the retry.
 */
const PUBLICATION_STATES: Record<string, true> = {
  in_review: true,
  approved: true,
  publishing: true,
  published: true,
  publish_failed: true,
};

function statePanel(app: AppShell, draft: api.DraftRow, isAuthor: boolean): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Draft state</h2></div>
      <div class="panel-body">
        <dl class="dl">
          <dt>State</dt>
          <dd>${tag(DRAFT_STATE_LABEL[draft.state] ?? draft.state, 'D', draft.state === 'publish_failed' ? 'danger' : 'plain')}</dd>
          <dt>Version</dt>
          <dd>${draft.version}</dd>
          <dt>Author</dt>
          <dd>${personLabel(draft.author_id, app.state.directory)}</dd>
          <dt>Updated</dt>
          <dd>${formatDateTime(draft.updated_at)}</dd>
          <dt>Base release</dt>
          <dd>${draft.base_release_id ?? 'none'}</dd>
        </dl>
        ${
          isAuthor
            ? ''
            : html`<div class="callout">
              <span class="callout-tag">Not yours</span>
              <p>You did not write this draft, so the save control is unavailable to you.</p>
            </div>`
        }
        ${
          // A FAILED PUBLICATION IS VISIBLE HERE AND INERT, and inert is the
          // defect. PUB-04 asks for a failure to be retryable, and until this
          // link the path was: library row -> editor -> a red badge and nothing
          // to press. The retry itself is deliberately NOT duplicated here —
          // one control, one availability rule — so this panel says where it is.
          PUBLICATION_STATES[draft.state] === true
            ? html`<div class="callout${draft.state === 'publish_failed' ? ' callout-danger' : ''}">
                <span class="callout-tag">${DRAFT_STATE_LABEL[draft.state] ?? draft.state}</span>
                <p>
                  ${draft.state === 'publish_failed'
                    ? 'This draft failed to publish. The reason for the failure, and the control that publishes it again, are both on the review screen.'
                    : 'Publication is a separate step, taken on the review screen. Nothing on this screen publishes anything.'}
                </p>
                <p>
                  <a href="/o/${app.orgSlug}/reviews/${draft.id}">Open the review and publication panel</a>
                </p>
              </div>`
            : ''
        }
      </div>
    </div>
  `;
}

function savePanel(app: AppShell, draft: api.DraftRow, isAuthor: boolean): RawHtml {
  const canSubmit = isAuthor && draft.state === 'draft';
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Save and submit</h2></div>
      <div class="panel-body stack-sm">
        <label class="field-label" for="editor-reason">Change reason</label>
        <textarea
          class="field"
          id="editor-reason"
          rows="4"
          data-field="editor.note"
          placeholder="What this revision changes and why"
        >${app.state.note}</textarea>
        <div class="btn-row">
          ${button('Save draft', 'editor:save', 'primary', isAuthor ? {} : { disabled: 'disabled' })}
          ${button('Submit for review', 'editor:submit', 'secondary', canSubmit ? {} : { disabled: 'disabled' })}
        </div>
        <p class="cell-note">
          Saving keeps the draft private to you. Submitting moves it to review, where a different person
          must decide it.
        </p>
        <div aria-live="polite">
          ${app.state.notice === null ? '' : html`<p class="cell-note">${app.state.notice.text}</p>`}
        </div>
      </div>
    </div>
  `;
}
